import ActionTextIcon from "@/components/icons/ActionTextIcon";
import InfoIcon from "@/components/icons/InfoIcon";
import DaysCountTag from "@/components/tags/DaysCountTag";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDate } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero, toDate } from "@/utils/utils";
import { Card, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useFilterAgentEndingCompetences } from "../../../../common/api/queries";
import { TableSizes } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { useHasPermissions } from "../../../auth/hooks";
import { AgentCompetenceType } from "../../enums";
import { AgentCompetenceFilterPageRequest, AgentCompetenceIntervalList, AgentCompetenceList } from "../../types";
import { formatAgentIdNumber } from "../../utils";
import { AgentCompetenceForm } from "../forms/AgentCompetenceForm";
import { AgentTypeTagWithName } from "../tags/AgentTypeTagWithName";

interface Props {
  searchParams: AgentCompetenceFilterPageRequest;
}

export const AgentEndingCompetenceTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasAgentCompetenceManagePermission = useHasPermissions(Permission.AGENT_COMPETENCE_MANAGE);

  const [competenceToUpdate, setCompetenceToUpdate] = useState<AgentCompetenceList>();

  const { data, isFetching } = useFilterAgentEndingCompetences(searchParams);

  const columns: ColumnsType<AgentCompetenceIntervalList> = useMemo(() => {
    const items: ColumnsType<AgentCompetenceIntervalList> = [
      {
        key: "days",
        width: 100,
        render: (_, record) => (
          <DaysCountTag
            days={(toDate(record.endDate) as Dayjs).startOf("day").diff(dayjs().startOf("day"), "days")}
            style={{ marginRight: 0 }}
          />
        ),
      },
      {
        key: "aggregatedName",
        title: t("agent.attrs.aggregatedName"),
        width: 250,
        ellipsis: { showTitle: false },
        render: (_, record) => <AgentTypeTagWithName agent={record.competence.agent} hideIdNumber ellipsis showLink />,
      },
      {
        key: "idNumber",
        title: t("agent.attrs.idNumber"),
        width: 90,
        render: (_, record) => formatAgentIdNumber(record.competence.agent),
      },
      {
        key: "type",
        title: t("agent.competence.enums.competenceType._label"),
        ellipsis: { showTitle: false },
        width: 70,
        render: (_, record) => (
          <Ellipsis>{t("agent.competence.enums.competenceType." + record.competence.type)}</Ellipsis>
        ),
      },
      {
        key: "label",
        title: t("agent.competence.attrs.label"),
        ellipsis: { showTitle: false },
        width: 170,
        render: (_, record) =>
          record.competence.type === AgentCompetenceType.OTHER ? (
            <Ellipsis>{record.competence.label}</Ellipsis>
          ) : (
            <>
              <Ellipsis>
                <span>{t("agent.enums.financialSector." + record.competence.sector)}</span>
              </Ellipsis>
              <br />
              <Ellipsis>
                <span>{t("agent.competence.helpers.competenceLevel." + record.competence.level)}</span>
              </Ellipsis>
            </>
          ),
      },
      {
        key: "startDate",
        title: t("agent.competence.attrs.intervals.startDate"),
        width: 95,
        render: (_, record) => formatLocaleDate(record.startDate),
      },
      {
        key: "endDate",
        title: t("agent.competence.attrs.intervals.endDate"),
        width: 95,
        render: (_, record) => formatLocaleDate(record.endDate),
      },
      {
        key: "provider",
        title: t("agent.competence.enums.competenceProvider._label"),
        ellipsis: { showTitle: false },
        width: 150,
        render: (_, record) =>
          record.provider ? (
            <Ellipsis>{t("agent.competence.enums.competenceProvider." + record.provider)}</Ellipsis>
          ) : null,
      },
      {
        key: "note",
        align: "center",
        width: 40,
        render: (_, record) =>
          record.competence.note ? (
            <InfoIcon title={t("agent.competence.attrs.note")} tooltip={record.competence.note} />
          ) : null,
      },
    ];

    if (hasAgentCompetenceManagePermission) {
      items.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 90,
        render: (_, record) => (
          <ActionTextIcon
            icon="edit"
            color="blue"
            text={t("common.edit")}
            onClick={() => setCompetenceToUpdate(record.competence)}
          />
        ),
      });
    }

    return items;
  }, [hasAgentCompetenceManagePermission]);

  return (
    <>
      <Card className="card-box">
        <Table<AgentCompetenceIntervalList>
          {...tableStandardProps()}
          columns={columns}
          loading={isFetching}
          scroll={{ x: TableSizes.LARGE }}
          dataSource={data?.pageData}
          pagination={{
            ...paginationTableProps,
            current: numberOrZero(data?.pageIndex) + 1,
            pageSize: data?.pageSize,
            total: data?.totalElementsCount,
            onChange: (pageIndex) =>
              navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
          }}
        />
      </Card>

      {hasAgentCompetenceManagePermission && (
        <AgentCompetenceForm
          open={!!competenceToUpdate}
          agentId={competenceToUpdate?.agent.id}
          competence={competenceToUpdate}
          onFormCancel={() => setCompetenceToUpdate(undefined)}
        />
      )}
    </>
  );
};
